import { Button, Input, Modal, notify } from '@affine/component';
import { getAFFiNEWorkspaceSchema } from '@affine/core/modules/workspace/global-schema';
import { useI18n } from '@affine/i18n';
import {
  docLinkBaseURLMiddleware,
  embedSyncedDocMiddleware,
  MarkdownAdapterFactoryIdentifier,
  titleMiddleware,
} from '@blocksuite/affine/shared/adapters';
import { Transformer } from '@blocksuite/affine/store';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { DocsService } from '../../../modules/doc';
import { WorkspaceService } from '../../../modules/workspace';
import {
  type DiscourseCategory,
  useDiscoursePublish,
} from './use-discourse-publish';
import * as styles from './publish-dialog.css';

interface PublishDialogProps {
  docId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PublishDialog = ({
  docId,
  open,
  onOpenChange,
}: PublishDialogProps) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const workspaceService = useService(WorkspaceService);
  const doc = useLiveData(docsService.list.doc$(docId));
  const docTitle = useLiveData(doc?.title$) || 'Untitled';

  const { fetchCategories, publishTopic, loading, error } =
    useDiscoursePublish();

  const [title, setTitle] = useState(docTitle);
  const [categories, setCategories] = useState<DiscourseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [tags, setTags] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(docTitle);
      setTags('');
      setCategoriesLoading(true);
      fetchCategories()
        .then(cats => {
          setCategories(cats);
          if (cats.length > 0 && !selectedCategory) {
            setSelectedCategory(String(cats[0].id));
          }
        })
        .catch(err => {
          console.error('Failed to fetch categories:', err);
          notify.error({
            title: 'Failed to load categories',
            message: err.message || 'Please try again later',
          });
        })
        .finally(() => {
          setCategoriesLoading(false);
        });
    }
  }, [open, docTitle, fetchCategories, selectedCategory]);

  const getMarkdownContent = useCallback(async (): Promise<string> => {
    const workspace = workspaceService.workspace;
    const blocksuiteDoc = workspace.docCollection.getDoc(docId);

    if (!blocksuiteDoc) {
      throw new Error('Document not found');
    }

    const store = blocksuiteDoc.getStore();
    if (!store) {
      throw new Error('Document store not found');
    }

    const transformer = new Transformer({
      schema: getAFFiNEWorkspaceSchema(),
      blobCRUD: store.workspace.blobSync,
      docCRUD: {
        create: (id: string) =>
          store.workspace.createDoc(id).getStore({ id }),
        get: (id: string) =>
          store.workspace.getDoc(id)?.getStore({ id }) ?? null,
        delete: (id: string) => store.workspace.removeDoc(id),
      },
      middlewares: [
        docLinkBaseURLMiddleware(store.workspace.id),
        titleMiddleware(store.workspace.meta.docMetas),
        embedSyncedDocMiddleware('content'),
      ],
    });

    const adapterFactory = store.provider.get(MarkdownAdapterFactoryIdentifier);
    const adapter = adapterFactory.get(transformer);
    const result = await adapter.fromDoc(store);

    return result?.file || '';
  }, [docId, workspaceService]);

  // Extract blob references from markdown content
  // Returns array of { key, fullRef } where key is the blob hash and fullRef is the full markdown reference
  const extractBlobRefs = useCallback(
    (markdown: string): Array<{ key: string; fullRef: string; ext: string }> => {
      const blobRefs: Array<{ key: string; fullRef: string; ext: string }> = [];
      const seen = new Set<string>();

      // Pattern for image references: ![...](assets/hashKey.extension)
      const imagePattern = /!\[[^\]]*\]\((assets\/[^)]+)\)/g;
      let match;

      while ((match = imagePattern.exec(markdown)) !== null) {
        const fullRef = match[1]; // e.g., "assets/abc123=.png"
        // Filter out http/https URLs - we only want blob references
        if (
          !fullRef.startsWith('http://') &&
          !fullRef.startsWith('https://')
        ) {
          // Extract the key and extension
          const withoutPrefix = fullRef.replace(/^assets\//, '');
          const extMatch = withoutPrefix.match(/\.([a-zA-Z]+)$/);
          const ext = extMatch ? extMatch[1] : 'png';
          const key = withoutPrefix.replace(/\.[a-zA-Z]+$/, '');

          if (key && !seen.has(key)) {
            seen.add(key);
            blobRefs.push({ key, fullRef, ext });
          }
        }
      }

      return blobRefs;
    },
    []
  );

  // Read blob data from the workspace
  const readBlobData = useCallback(
    async (
      blobKey: string
    ): Promise<{ data: string; contentType: string } | null> => {
      try {
        const workspace = workspaceService.workspace;
        const blobStorage = workspace.docCollection.blobSync;

        const blob = await blobStorage.get(blobKey);
        if (!blob) {
          console.warn(`Blob not found: ${blobKey}`);
          return null;
        }

        // Convert blob to base64 using FileReader (handles large files)
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            // Remove data URL prefix (e.g., "data:image/png;base64,")
            const base64Data = result.split(',')[1] || result;
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        return {
          data: base64,
          contentType: blob.type || 'image/png',
        };
      } catch (err) {
        console.error(`Failed to read blob ${blobKey}:`, err);
        return null;
      }
    },
    [workspaceService]
  );

  const handlePublish = useCallback(async () => {
    if (!selectedCategory) {
      notify.error({
        title: 'Please select a category',
      });
      return;
    }

    if (!title.trim()) {
      notify.error({
        title: 'Please enter a title',
      });
      return;
    }

    setPublishing(true);
    try {
      const markdown = await getMarkdownContent();

      if (!markdown.trim()) {
        notify.error({
          title: 'Document is empty',
          message: 'Cannot publish an empty document',
        });
        return;
      }

      const tagList = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Debug: Log the markdown to see image format
      console.log('Markdown content:', markdown);

      // Extract blob references and read blob data from frontend
      const blobRefs = extractBlobRefs(markdown);
      console.log('Found blob refs:', blobRefs);

      // Read blob data from the workspace
      const blobs: Array<{
        key: string;
        fullRef: string;
        ext: string;
        data: string;
        contentType: string;
      }> = [];

      for (const ref of blobRefs) {
        const blobData = await readBlobData(ref.key);
        if (blobData) {
          blobs.push({
            ...ref,
            data: blobData.data,
            contentType: blobData.contentType,
          });
          console.log(`Read blob ${ref.key}: ${blobData.contentType}`);
        }
      }

      console.log('Publishing with blobs:', blobs.length);

      const result = await publishTopic(
        title.trim(),
        markdown,
        Number(selectedCategory),
        tagList.length > 0 ? tagList : undefined,
        blobs.length > 0 ? blobs : undefined
      );

      notify.success({
        title: 'Published successfully!',
        message: 'Your document has been published to yoDEV',
        action: {
          label: 'View',
          onClick: () => {
            window.open(result.topicUrl, '_blank');
          },
        },
      });

      onOpenChange(false);
    } catch (err) {
      console.error('Failed to publish:', err);
      notify.error({
        title: 'Failed to publish',
        message: err instanceof Error ? err.message : 'Please try again later',
      });
    } finally {
      setPublishing(false);
    }
  }, [
    selectedCategory,
    title,
    tags,
    getMarkdownContent,
    publishTopic,
    onOpenChange,
    extractBlobRefs,
    readBlobData,
  ]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const isLoading = loading || publishing || categoriesLoading;

  return (
    <Modal
      open={open}
      title="Publish to yoDEV"
      onOpenChange={onOpenChange}
      width={480}
    >
      <div className={styles.content}>
        <div className={styles.field}>
          <label className={styles.label}>Title</label>
          <Input
            value={title}
            onChange={setTitle}
            placeholder="Enter a title for your post"
            disabled={isLoading}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Category</label>
          {categoriesLoading ? (
            <div className={styles.loadingText}>Loading categories...</div>
          ) : (
            <select
              className={styles.select}
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              disabled={isLoading}
            >
              {categories.map(cat => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Tags (optional)</label>
          <Input
            value={tags}
            onChange={setTags}
            placeholder="Enter tags separated by commas"
            disabled={isLoading}
          />
          <div className={styles.hint}>
            Separate multiple tags with commas (e.g., javascript, tutorial,
            react)
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.footer}>
        <Button size="large" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          size="large"
          variant="primary"
          onClick={handlePublish}
          disabled={isLoading || !selectedCategory || !title.trim()}
        >
          {publishing ? 'Publishing...' : 'Publish'}
        </Button>
      </div>
    </Modal>
  );
};
